import React, { createContext, useContext, useState, useEffect } from 'react';
import API, { setAuthToken, clearAuthToken } from '../utils/api';
import { getAffiliateCookieCode } from '../utils/affiliate';
import { initIap, logoutIap } from '../utils/iap';
import { isNative } from '../utils/capacitor';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// Safe localStorage wrapper — used only for the non-sensitive user object cache
// and the persisted token (for page-refresh re-hydration). The token is a JWT
// and has a finite expiry, so storage here reduces XSS risk to the same window
// as any auth session, while keeping the app usable across refreshes.
//
// Why not httpOnly cookie? The backend is cross-origin (Railway URL vs frontend
// domain), so SameSite=Lax cookies are not sent on cross-origin requests.
// SameSite=None would require Secure + careful CORS, adding complexity with no
// practical gain over a short-lived JWT that cannot mutate the DOM.
//
// The real XSS defence here is Content-Security-Policy on the frontend — not
// where the token is stored.
const storage = {
  get:    (key)        => { try { return localStorage.getItem(key); }  catch { return null; } },
  set:    (key, value) => { try { localStorage.setItem(key, value); }  catch {} },
  remove: (key)        => { try { localStorage.removeItem(key); }      catch {} },
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = storage.get('dreamybedtime_token');
    const savedUser  = storage.get('dreamybedtime_user');

    if (!savedToken) {
      // No token — user is not logged in. Nothing to verify.
      setLoading(false);
      return;
    }

    // Restore the in-memory token immediately so the /auth/me request is authenticated.
    setAuthToken(savedToken);

    // Optimistically hydrate the UI from the cached user object so the page
    // isn't blank while the server round-trip completes.
    if (savedUser) {
      try { setUser(JSON.parse(savedUser)); } catch (_) { /* corrupt cache — ignore */ }
    }

    API.get('/auth/me')
      .then(res => {
        setUser(res.data.user);
        storage.set('dreamybedtime_user', JSON.stringify(res.data.user));
      })
      .catch((err) => {
        // Only clear the session on an explicit 401 (token expired / invalid).
        // Network errors mean the server is temporarily unreachable — keep the
        // cached state so a brief connectivity blip doesn't log the user out.
        const status = err?.response?.status;
        if (status === 401) {
          clearAuthToken();
          storage.remove('dreamybedtime_token');
          storage.remove('dreamybedtime_user');
          setUser(null);
        }
        // 403 suspended / 503 maintenance: interceptor handles redirect — nothing to do here.
      })
      .finally(() => setLoading(false));
  }, []);

  // Associate in-app purchases with the logged-in account so RevenueCat webhook
  // events map back to this user. No-op on web.
  //
  // `_id || id`: the sign-in response used to carry only `id` (/auth/me sends
  // `_id`), so after every fresh sign-in this never ran. The purchase SDK stayed
  // anonymous, Apple charged the customer, the webhook could not map the
  // anonymous purchase to an account, and they stayed on Free.
  const iapUserId = user?._id || user?.id;
  useEffect(() => {
    if (iapUserId) initIap(iapUserId);
  }, [iapUserId]);

  const sendLoginOtp = async (email) => {
    const res = await API.post('/auth/login', { email });
    return res.data;
  };

  const register = async (name, email) => {
    const affiliateCode = getAffiliateCookieCode();
    const res = await API.post('/auth/register', { name, email, ...(affiliateCode && { affiliateCode }) });
    return res.data;
  };

  const verifyOtp = async (email, otp) => {
    const res = await API.post('/auth/verify-otp', { email, otp });
    setAuthToken(res.data.token);
    storage.set('dreamybedtime_token', res.data.token);
    storage.set('dreamybedtime_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    // The sign-in response is a trimmed user — no entitlement, co-parent or
    // admin fields — and the app kept using it until the next cold start: a
    // co-parent looked like a free user who could buy a plan that did nothing.
    // Load the full account now, without holding up sign-in. Dropped if the
    // user signed out (or in as someone else) before it arrived.
    API.get('/auth/me').then((me) => {
      if (storage.get('dreamybedtime_token') !== res.data.token) return;
      setUser(me.data.user);
      storage.set('dreamybedtime_user', JSON.stringify(me.data.user));
    }).catch(() => {});
    return res.data;
  };

  const logout = () => {
    // Rotate the server-side sessionId so the issued JWT stops working straight
    // away. Clearing local state alone left it valid for its full 7-day life,
    // which meant "log out" revoked nothing on a shared or compromised device.
    // Fire-and-forget: local state is cleared regardless of the network result.
    API.post('/auth/logout').catch(() => {});
    logoutIap();
    clearAuthToken();
    storage.remove('dreamybedtime_token');
    storage.remove('dreamybedtime_user');
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    storage.set('dreamybedtime_user', JSON.stringify(updatedUser));
  };

  const refreshUser = async () => {
    try {
      const res = await API.get('/auth/me');
      setUser(res.data.user);
      storage.set('dreamybedtime_user', JSON.stringify(res.data.user));
    } catch (_) { /* silent — caller handles UI state */ }
  };

  // A plan bought on another device — the website, or the other app — reached
  // this one only on its next cold start. An app suspended in the background
  // (iOS keeps them for days) went on showing Free right after the customer
  // paid on their laptop, and a cancelled plan stayed unlocked. Re-read the
  // account whenever the app or tab comes back to the foreground.
  const signedIn = !!iapUserId;
  useEffect(() => {
    if (!signedIn) return undefined;
    let last = Date.now();
    const onForeground = () => {
      if (Date.now() - last < 30000) return;        // tab flicks, duplicate events
      last = Date.now();
      refreshUser();
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') onForeground(); };
    document.addEventListener('visibilitychange', onVisibility);
    let handle = null;
    let stopped = false;
    if (isNative()) {
      import('@capacitor/app')
        .then(({ App }) => App.addListener('appStateChange', ({ isActive }) => { if (isActive) onForeground(); }))
        .then((h) => { if (stopped) h.remove(); else handle = h; })
        .catch(() => {});
    }
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (handle) handle.remove();
    };
  }, [signedIn]); // eslint-disable-line

  return (
    <AuthContext.Provider value={{ user, loading, sendLoginOtp, register, verifyOtp, logout, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
